export const LAUNCHPAD_STORAGE = "cue-studio-launchpad-v1";
export const LAUNCHPAD_CHANGED = "cue-studio-launchpad-changed";
export const KEY_COUNTS = [6, 9, 12, 15, 16, 24, 32] as const;
export type PadAction = "cue" | "blackout" | "clear" | "toggle" | "restart-section" | "reset";
export interface PadShortcut {
  code: string;
  ctrl: boolean;
  alt: boolean;
  shift: boolean;
  meta: boolean;
}
export interface LaunchpadKey {
  id: string;
  label: string;
  icon: string;
  picture: string;
  text: string;
  color: string;
  action: PadAction;
  layout: "card" | "banner";
  pause: boolean;
  durationSeconds: number;
  shortcut: PadShortcut | null;
  numpad: PadShortcut | null;
}
export interface LaunchpadConfig {
  version: 1;
  /** Cue pause defaults revision, independent of the portable configuration format. */
  cuePauseVersion: 1;
  count: number;
  shortcutsEnabled: boolean;
  keys: LaunchpadKey[];
}
const presets = [
  ["Hold", "pause", "hold", "Please hold"],
  ["Raise hand", "hand", "raise-hand", "Raise your hand"],
  ["Restart section", "restart", "restart-section", "Restart this section"],
  ["Slower", "turtle", "slower", "A little slower"],
  ["Louder", "volume-high", "louder", "Speak a little louder"],
  ["Look at camera", "camera", "look-camera", "Look at the camera"],
  ["Smile", "smile", "smile", "Give us a smile"],
  ["Blackout", "eye-off", "none", ""],
  ["Alert", "alert", "alert", "Please hold"],
  ["Faster", "zap", "faster", "A little faster"],
  ["Softer", "volume-low", "softer", "Speak a little softer"],
  ["Stand up", "person", "stand", "Please stand up"],
  ["Sit down", "person", "sit", "Please sit down"],
  ["Move left", "arrow-left", "move-left", "Move to your left"],
  ["Move right", "arrow-right", "move-right", "Move to your right"],
  ["Chin up", "arrow-up", "chin-up", "Chin up a little"],
  ["Chin down", "arrow-down", "chin-down", "Chin down a little"],
  ["Come closer", "closer", "closer", "Come a little closer"],
  ["Step back", "further", "step-back", "Take a step back"],
  ["Water break", "water", "water", "Take a water break"],
  ["Mic check", "mic", "mic-check", "Microphone check"],
  ["Quiet please", "volume-off", "quiet", "Quiet on set"],
  ["Applause", "hand", "applause", "Applause"],
  ["Three", "timer", "three", "3"],
  ["Two", "timer", "two", "2"],
  ["One", "timer", "one", "1"],
  ["Action", "play", "action", "Action"],
  ["Cut", "scissors", "cut", "Cut"],
  ["That's a wrap", "check", "wrap", "That's a wrap"],
  ["Breathe", "wind", "breathe", "Take a breath"],
  ["Listen", "ear", "listen", "Listen to the director"],
  ["Clear cue", "check", "none", ""],
] as const;
export function hardwareShortcut(index: number): PadShortcut {
  return { code: `F${13 + index % 12}`, shift: index >= 12, ctrl: index >= 24, alt: false, meta: false };
}
export function numpadShortcut(index: number): PadShortcut {
  return { code: `Numpad${1 + index % 9}`, shift: index >= 9 && index < 18, ctrl: index >= 18 && index < 27, alt: index >= 27, meta: false };
}
export function defaultLaunchpad(): LaunchpadConfig {
  return { version: 1, cuePauseVersion: 1, count: 9, shortcutsEnabled: true, keys: presets.map(([label, icon, picture, text], index) => ({
    id: `key-${index + 1}`, label, icon, picture, text, color: index === 8 ? "#ff8295" : "#ffd426",
    action: index === 7 ? "blackout" : index === 31 ? "clear" : "cue", layout: "card", pause: false,
    durationSeconds: 0, shortcut: hardwareShortcut(index), numpad: numpadShortcut(index),
  })) };
}
const codePattern = /^(?:Key[A-Z]|Digit[0-9]|F(?:[1-9]|1[0-9]|2[0-4])|Numpad(?:[0-9]|Add|Subtract|Multiply|Divide|Decimal|Enter))$/;
export function normalizeShortcut(value: unknown): PadShortcut | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const x = value as Record<string, unknown>;
  if (typeof x.code !== "string" || !codePattern.test(x.code)) return null;
  return { code: x.code, ctrl: x.ctrl === true, alt: x.alt === true, shift: x.shift === true, meta: x.meta === true };
}
export function shortcutId(value: PadShortcut | null): string {
  return value ? `${Number(value.ctrl)}${Number(value.alt)}${Number(value.shift)}${Number(value.meta)}:${value.code}` : "";
}
export function shortcutLabel(value: PadShortcut | null): string {
  if (!value) return "Not assigned";
  const key = value.code.replace(/^Key/, "").replace(/^Digit/, "").replace(/^Numpad/, "Num ");
  return [value.ctrl && "Ctrl", value.alt && "Alt", value.shift && "Shift", value.meta && "Cmd", key].filter(Boolean).join(" + ");
}
export function eventShortcut(event: Pick<KeyboardEvent, "code" | "ctrlKey" | "altKey" | "shiftKey" | "metaKey">): PadShortcut | null {
  return normalizeShortcut({ code: event.code, ctrl: event.ctrlKey, alt: event.altKey, shift: event.shiftKey, meta: event.metaKey });
}
export function matchesShortcut(event: KeyboardEvent, value: PadShortcut | null) {
  return !!value && shortcutId(eventShortcut(event)) === shortcutId(value);
}
export function shortcutProblem(value: PadShortcut | null): string | null {
  if (!value) return null;
  if (value.meta || (value.ctrl && !value.alt && /^(Key[TLNRWQFPHJUSDOEB]|Digit[0-9])$/.test(value.code)) || value.code === "F5" || value.code === "F11" || value.code === "F12" || (value.alt && value.code === "F4"))
    return "This shortcut is reserved by the browser or operating system. Try F13–F24 or add other modifiers.";
  if (!value.ctrl && !value.alt && !value.shift && value.code === "KeyB") return "B is already used for Blackout. Choose another key or add a modifier.";
  return null;
}
export function bindingConflict(keys: LaunchpadKey[], keyId: string, binding: PadShortcut | null): string | null {
  if (!binding) return null;
  const id = shortcutId(binding);
  const other = keys.find(key => key.id !== keyId && [key.shortcut, key.numpad].some(value => shortcutId(value) === id));
  return other ? `Already assigned to ${other.label} (${other.id.replace("key-", "key ")}).` : null;
}
export function normalizeLaunchpad(value: unknown): LaunchpadConfig {
  const base = defaultLaunchpad();
  if (!value || typeof value !== "object" || Array.isArray(value)) return base;
  const object = value as Record<string, unknown>;
  if (!Array.isArray(object.keys)) return base;
  // Legacy configs saved pause:true for every preset without recording whether
  // it was an explicit choice. Move their cue keys to the new non-pausing baseline
  // once, including hidden/customized cues. The revision travels with saves and
  // backups, so any Pause opt-in made after migration survives normalization.
  const migrateCuePause = object.cuePauseVersion !== base.cuePauseVersion;
  const cleanText = (v: unknown, fallback: string, max: number) => typeof v === "string" ? v.slice(0, max).replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f]/g, "") : fallback;
  const slug = (v: unknown, fallback: string) => typeof v === "string" && v.length <= 64 && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(v) ? v : fallback;
  const actions: PadAction[] = ["cue", "blackout", "clear", "toggle", "restart-section", "reset"];
  const keys = base.keys.map((fallback, index) => {
    const raw = (object.keys as unknown[])[index];
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return fallback;
    const x = raw as Record<string, unknown>;
    const action = actions.includes(x.action as PadAction) ? x.action as PadAction : fallback.action;
    return { ...fallback, label: cleanText(x.label, fallback.label, 36).trim() || `Key ${index + 1}`,
      text: cleanText(x.text, fallback.text, 500), icon: slug(x.icon, fallback.icon), picture: slug(x.picture, fallback.picture),
      color: typeof x.color === "string" && /^#[a-f0-9]{6}$/i.test(x.color) ? x.color : fallback.color,
      action,
      layout: x.layout === "banner" ? "banner" as const : "card" as const,
      pause: migrateCuePause && action === "cue" ? false : typeof x.pause === "boolean" ? x.pause : fallback.pause,
      durationSeconds: typeof x.durationSeconds === "number" && Number.isFinite(x.durationSeconds) ? Math.max(0, Math.min(120, Math.round(x.durationSeconds))) : 0,
      shortcut: x.shortcut === undefined ? fallback.shortcut : normalizeShortcut(x.shortcut),
      numpad: x.numpad === undefined ? fallback.numpad : normalizeShortcut(x.numpad),
    };
  });
  // First assignment wins when importing hand-edited or older configuration.
  const used = new Set<string>();
  for (const key of keys) for (const field of ["shortcut", "numpad"] as const) {
    const id = shortcutId(key[field]);
    if (id && (used.has(id) || shortcutProblem(key[field]))) key[field] = null;
    else if (id) used.add(id);
  }
  return { version: 1, cuePauseVersion: 1, keys, count: typeof object.count === "number" && Number.isFinite(object.count) ? Math.max(6, Math.min(32, Math.round(object.count))) : 9,
    shortcutsEnabled: typeof object.shortcutsEnabled === "boolean" ? object.shortcutsEnabled : true };
}
export function readLaunchpad(): LaunchpadConfig {
  try { return normalizeLaunchpad(JSON.parse(localStorage.getItem(LAUNCHPAD_STORAGE) || "null")); }
  catch { return defaultLaunchpad(); }
}
export function saveLaunchpad(value: LaunchpadConfig) {
  const normalized = normalizeLaunchpad(value);
  localStorage.setItem(LAUNCHPAD_STORAGE, JSON.stringify(normalized));
  window.dispatchEvent(new CustomEvent(LAUNCHPAD_CHANGED));
  return normalized;
}
